import { Alert, Card, Table, Tabs, Typography } from "antd";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../services/api";
import { fmtDateTime, fmtNumber, StatusTag } from "../../components/format";

interface CharacterRow {
  id: string;
  key: string;
  displayName: string;
  status: string;
  publishedVersion: { version: number; policyVersion: number; createdAt: string | null; createdBy: string | null } | null;
  counts: Record<string, number>;
  createdAt: string | null;
}

export function CharactersPage() {
  const [tab, setTab] = useState("characters");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "characters", label: "角色", children: <CharacterList /> },
        { key: "backgrounds", label: "背景图", children: <BackgroundList /> },
      ]}
    />
  );
}

function CharacterList() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["characters"],
    queryFn: () => api.get<CharacterRow[]>("/api/v1/admin/characters"),
  });
  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  return (
    <Card styles={{ body: { padding: 12 } }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        `CharacterVersion` 是线上直接改 prompt 的表，工作方式类似 Git：保存即 INSERT 一行新版本并移动
        publishedVersionId，旧行标记 superseded 保留。已发布的行不可原地修改——这样才能追溯「上周那条回复用的是哪版设定」。
      </Typography.Paragraph>
      <Table<CharacterRow>
        size="small"
        rowKey="id"
        loading={isLoading}
        pagination={false}
        dataSource={data ?? []}
        columns={[
          {
            title: "角色",
            dataIndex: "displayName",
            render: (v: string, r) => <Link to={`/characters/${r.id}`}>{v}</Link>,
          },
          { title: "key", dataIndex: "key", width: 130 },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
          {
            title: "已发布版本",
            width: 110,
            render: (_, r) => (r.publishedVersion ? `v${r.publishedVersion.version}` : "—"),
          },
          {
            title: "定价策略版本",
            width: 120,
            render: (_, r) => (r.publishedVersion ? `v${r.publishedVersion.policyVersion}` : "—"),
          },
          { title: "版本数", width: 90, align: "right", render: (_, r) => r.counts["versions"] },
          { title: "外观", width: 80, align: "right", render: (_, r) => r.counts["appearances"] },
          { title: "关系", width: 90, align: "right", render: (_, r) => fmtNumber(r.counts["relationships"]) },
          { title: "日记", width: 80, align: "right", render: (_, r) => fmtNumber(r.counts["diaries"]) },
          { title: "知识库", width: 80, align: "right", render: (_, r) => r.counts["knowledgeBases"] },
          {
            title: "最后发布",
            width: 170,
            render: (_, r) => fmtDateTime(r.publishedVersion?.createdAt ?? null),
          },
        ]}
      />
    </Card>
  );
}

function BackgroundList() {
  const { data, isLoading } = useQuery({
    queryKey: ["backgrounds"],
    queryFn: () => api.get<Record<string, unknown>[]>("/api/v1/admin/characters/backgrounds"),
  });
  return (
    <Card styles={{ body: { padding: 12 } }}>
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: "尚无背景图（BackgroundImage 表为空）" }}
        dataSource={data ?? []}
        columns={[
          { title: "名称", dataIndex: "displayName" },
          { title: "可见性", dataIndex: "visibility", width: 110, render: (v) => <StatusTag value={v} /> },
          { title: "来源", dataIndex: "sourceKind", width: 130 },
          { title: "排序", dataIndex: "sortOrder", width: 80, align: "right" },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
        ]}
      />
    </Card>
  );
}
